import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { deleteReport, getReports, setReportStatus } from "../../api/adminService";
import { ReportStatusLabels, ReportStatusColors, ReportTypeLabels, formatDate } from "./constants";

export default function Reports() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  function loadReports() {
    setLoading(true);
    getReports({
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load reports. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadReports();
  }

  function openDetail(row) {
    setSelected(row);
    setNoteDraft(row.adminNote || "");
  }

  async function handleStatusChange(row, status) {
    const updated = await setReportStatus(row.reportId, status, row.adminNote);
    setRows((prev) => prev.map((r) => (r.reportId === row.reportId ? { ...r, ...updated } : r)));
    if (selected?.reportId === row.reportId) {
      setSelected((prev) => ({ ...prev, ...updated }));
    }
  }

  async function handleSaveNote() {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await setReportStatus(selected.reportId, selected.status, noteDraft);
      setRows((prev) => prev.map((r) => (r.reportId === selected.reportId ? { ...r, ...updated } : r)));
      setSelected((prev) => ({ ...prev, ...updated }));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    await deleteReport(row.reportId);
    setRows((prev) => prev.filter((r) => r.reportId !== row.reportId));
    setRowCount((prev) => prev - 1);
    if (selected?.reportId === row.reportId) {
      setSelected(null);
    }
  }

  const columns = useMemo(
    () => [
      { field: "subject", headerName: "Subject", flex: 1, minWidth: 200 },
      {
        field: "type",
        headerName: "Type",
        width: 110,
        valueGetter: (v) => ReportTypeLabels[v] || v,
      },
      { field: "userFullName", headerName: "Reporter", width: 160, valueGetter: (v) => v || "-" },
      { field: "userPhone", headerName: "Phone", width: 130, valueGetter: (v) => v || "-" },
      {
        field: "orderNumber",
        headerName: "Related",
        width: 150,
        valueGetter: (v, row) => row.orderNumber || row.storeName || "-",
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => (
          <Chip
            size="small"
            label={ReportStatusLabels[params.value] || params.value}
            color={ReportStatusColors[params.value] || "default"}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: "Received",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Reports &amp; Cases</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack
        component="form"
        onSubmit={handleSearchSubmit}
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Search subject, description, name or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 320 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <MenuItem value="">All types</MenuItem>
            {Object.entries(ReportTypeLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(ReportStatusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.reportId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row)}
          sx={{ border: "none" }}
        />
      </Box>

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected && (
          <Box sx={{ width: 380, p: 3 }}>
            <Typography variant="h6">{selected.subject}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Received {formatDate(selected.createdAt)}
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <DetailRow label="Type" value={ReportTypeLabels[selected.type] || selected.type} />
              <DetailRow label="Reporter" value={selected.userFullName || "-"} />
              <DetailRow label="Phone" value={selected.userPhone || "-"} />
              {selected.orderNumber && <DetailRow label="Order" value={`#${selected.orderNumber}`} />}
              {selected.storeName && <DetailRow label="Store" value={selected.storeName} />}
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, whiteSpace: "pre-wrap" }}>
              {selected.description}
            </Typography>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={selected.status}
                onChange={(e) => handleStatusChange(selected, e.target.value)}
              >
                {Object.entries(ReportStatusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Admin Note"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={handleSaveNote} disabled={saving} fullWidth>
                Save Note
              </Button>
              <Button color="error" onClick={() => handleDelete(selected)} fullWidth>
                Delete
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
